import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Headphones, Play, Square, Users, Volume2, VolumeX } from "lucide-react";
import { Badge } from "../components/ui/badge";
import { StatusPill } from "../components/StatusPill";
import { BigActionButton } from "../components/BigActionButton";
import { WaveformBars } from "../components/WaveformBars";
import { TranscriptPanel } from "../components/TranscriptPanel";
import { useListener } from "../hooks/useListener";

export default function Listener() {
  const navigate = useNavigate();
  const l = useListener();
  const isListening = l.phase === "listening";

  const statusState = l.live ? "live" : "wait";
  const statusLabel = l.live ? "Yayın aktif" : "Yayın bekleniyor";

  const ctaState = l.phase === "connecting" ? "connecting" : isListening ? "live" : "idle";
  const ctaLabel = l.phase === "connecting" ? "BAĞLANIYOR…" : isListening ? "DİNLEMEYİ DURDUR" : l.phase === "error" ? "TEKRAR DENE" : "ÇEVİRİYİ DİNLE";

  return (
    <div className="min-h-screen bg-background text-foreground">
      <main className="mx-auto flex min-h-screen max-w-[520px] flex-col gap-5 px-4 pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-6">
        {/* Top bar */}
        <div className="flex items-center justify-between">
          <button
            data-testid="listener-back-button"
            onClick={() => { l.stop(); navigate("/"); }}
            aria-label="Geri"
            className="flex h-11 w-11 items-center justify-center rounded-full border border-border/70 bg-secondary/40 text-muted-foreground transition-colors hover:text-foreground"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <Badge variant="secondary" className="font-mono text-xs" style={{ fontFamily: "'Roboto Mono', monospace" }}>DEMO01</Badge>
          <StatusPill state={statusState} label={statusLabel} testId="listener-broadcast-status-pill" />
        </div>

        {/* Header */}
        <div className="rounded-[var(--radius)] border border-border/80 bg-card/80 px-5 py-6 text-center backdrop-blur">
          <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full border border-border/70 bg-[hsl(var(--accent))]/10 text-[hsl(var(--accent))]">
            <Headphones className="h-6 w-6" />
          </div>
          <h1 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif" }}>
            CANLI ÇEVİRİ
          </h1>
          <p data-testid="listener-direction-label" className="mt-1.5 text-base text-muted-foreground">{l.directionLabel}</p>
          <div className="mt-3 flex items-center justify-center gap-3 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1">
              <Users className="h-3.5 w-3.5" />
              {l.listeners} dinleyici
            </span>
          </div>
        </div>

        {/* Waveform */}
        <WaveformBars active={l.audioActive && !l.muted} testId="listener-waveform" />

        {/* Primary CTA + mute toggle */}
        <div className="flex items-stretch gap-3">
          <div className="flex-1">
            <BigActionButton
              state={ctaState}
              onClick={isListening ? l.stop : l.start}
              icon={isListening ? Square : Play}
              testId="listener-play-button"
            >
              {ctaLabel}
            </BigActionButton>
          </div>
          <button
            data-testid="listener-mute-button"
            onClick={l.toggleMute}
            aria-label={l.muted ? "Sesi aç" : "Sesi kapat"}
            title={l.muted ? "Sesi aç" : "Sesi kapat (sadece yazılı çeviri)"}
            className={`flex w-16 shrink-0 items-center justify-center rounded-[var(--radius)] border transition-colors active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background sm:w-[72px] ${
              l.muted
                ? "border-[hsl(var(--state-error))]/50 bg-[hsl(var(--state-error))]/10 text-[hsl(var(--state-error))]"
                : "border-border/70 bg-secondary/60 text-foreground hover:ring-1 hover:ring-ring/40"
            }`}
          >
            {l.muted ? <VolumeX className="h-6 w-6" /> : <Volume2 className="h-6 w-6" />}
          </button>
        </div>

        {l.muted && (
          <div data-testid="listener-muted-banner" className="flex items-center justify-center gap-2 rounded-[calc(var(--radius)-4px)] border border-border/70 bg-secondary/40 px-3 py-2 text-sm text-muted-foreground">
            <VolumeX className="h-4 w-4" />
            Ses kapalı — çeviri sadece yazılı olarak akıyor
          </div>
        )}

        {l.error && (
          <div data-testid="listener-connection-banner" className="rounded-[calc(var(--radius)-4px)] border border-destructive/40 bg-destructive/10 px-3 py-2.5 text-sm text-[hsl(var(--state-error))]">
            {l.error}
          </div>
        )}

        {isListening && !l.live && (
          <div data-testid="listener-waiting-banner" className="rounded-[calc(var(--radius)-4px)] border border-border/70 bg-secondary/40 px-3 py-2.5 text-center text-sm text-muted-foreground">
            Konuşmacının yayını başlatması bekleniyor…
          </div>
        )}

        {/* Transcript */}
        <div className="space-y-2">
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Çeviri Metni</p>
          <TranscriptPanel
            lines={l.transcriptLines}
            partial={l.transcriptPartial}
            emptyText="Çeviri metni burada görünecek."
            testId="listener-translation-transcript"
            maxHeight="260px"
          />
        </div>
      </main>
    </div>
  );
}
